import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getStoolTestResults } from '../services/labService';
import MainHeader from '../Components/MainHeader';

const TestResultsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#4CAF50'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StoolIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const ResultCard = styled.div`
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  background-color: #00BBD3;
  padding: 16px 20px;
  color: white;
  text-align: center;
`;

const HeaderText = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  flex: 1;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
  text-align: right;
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e1e5e9;
  margin: 16px 0;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin: 20px 0 12px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StoolTestResultsPage = () => {
  const navigate = useNavigate();
  const [stoolResults, setStoolResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStoolResults = async () => {
      try {
        setLoading(true);
        const response = await getStoolTestResults();

        console.log('Stool Test Response:', response);

        if (response?.result && Array.isArray(response.list)) {
          // Transform API data to match frontend format
          const formattedResults = response.list.map(item => {
            const results = {};

            // Map API fields to Thai labels
            if (item.stool_color) results['สีของอุจจาระ'] = item.stool_color;
            if (item.stool_consistency) results['ลักษณะ'] = item.stool_consistency;
            if (item.stool_mucus) results['เมือกปน'] = item.stool_mucus;
            if (item.stool_blood) results['เลือดปน'] = item.stool_blood;
            if (item.stool_rbc) results['เม็ดเลือดแดง'] = item.stool_rbc;
            if (item.stool_wbc) results['เม็ดเลือดขาว'] = item.stool_wbc;
            if (item.stool_parasite) results['พยาธิ'] = item.stool_parasite;

            return {
              date: item.visit_date || item.dateadd || '-',
              hospital: item.hospital_name || 'โรงพยาบาล',
              results: results
            };
          });

          setStoolResults(formattedResults);
        }
      } catch (error) {
        console.error('Error loading stool results:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStoolResults();
  }, []);

  return (
    <TestResultsContainer>
      <MainHeader />

      <TopBar>ผลตรวจอุจจาระ</TopBar>

      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ผลตรวจอุจจาระ (Stool Test)</PageTitle>
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : stoolResults.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            ไม่พบข้อมูลผลตรวจอุจจาระ
          </div>
        ) : (
          <>
            {/* Status Card */}
            <StatusCard $bgColor="#4CAF50">
              <StoolIcon src="./ผลการตรวจอุจาระสี.png" alt="ผลตรวจอุจจาระ" />
              <StatusInfo>
                <StatusTitle style={{ color: 'white' }}>ผลตรวจล่าสุด</StatusTitle>
              </StatusInfo>
            </StatusCard>

            {stoolResults.map((result, index) => (
            <ResultCard key={index}>
              <CardHeader>
                <HeaderText>ผลตรวจอุจจาระ</HeaderText>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <InfoLabel bold>วันที่ตรวจ</InfoLabel>
                  <InfoValue>{result.date}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel bold>ตรวจที่</InfoLabel>
                  <InfoValue>{result.hospital}</InfoValue>
                </InfoRow>

                <Divider />

                <SectionTitle>รายการตรวจ</SectionTitle>

                {Object.entries(result.results).map(([label, value], idx) => (
                  <InfoRow key={idx}>
                    <InfoLabel>{label}</InfoLabel>
                    <InfoValue>{value}</InfoValue>
                  </InfoRow>
                ))}
              </CardContent>
            </ResultCard>
          ))}
          </>
        )}
      </Content>
    </TestResultsContainer>
  );
};

export default StoolTestResultsPage;
